/**
 * Utility for calculating token cost display values
 * Handles multi-generation scenarios where the pricing formula may or may not include num_images
 */

export interface MultiGenerationCostResult {
  totalCost: number;
  perRequestCost: number;
  requestCount: number;
  isMultiGeneration: true;
}

export type TokenCostDisplayResult = number | MultiGenerationCostResult;

/**
 * Calculate token cost display values for multi-generation scenarios
 *
 * @param baseCost - The cost calculated from the pricing formula
 * @param numGenerations - Number of generations requested (num_images)
 * @param pricingFormula - The pricing formula string (to check if it includes num_images)
 * @returns Either a simple number (for single generation) or MultiGenerationCostResult
 */
/**
 * Check whether a pricing formula requires input images to be uploaded
 * before the price preview can be calculated.
 *
 * Mandatory variables: input_img_mpx, input_img_count.
 * The optional input_img_count_may_zero variant evaluates to 0 without
 * images and must NOT block the preview.
 */
export function formulaRequiresInputImages(pricingFormula: string): boolean {
  return /\b(input_img_mpx|input_img_count)\b(?!_may_zero)/.test(pricingFormula);
}

export function calculateTokenCostDisplay(
  baseCost: number,
  numGenerations: number,
  pricingFormula: string
): TokenCostDisplayResult {
  // Single generation - return simple cost
  if (numGenerations <= 1) {
    return baseCost;
  }

  // Check if formula already includes num_images/gen_count multiplication
  const formulaIncludesNumImages =
    pricingFormula.includes('num_images') ||
    pricingFormula.includes('gen_count');

  if (formulaIncludesNumImages) {
    // Formula already calculates total cost - divide to get per request
    return {
      totalCost: baseCost,
      perRequestCost: Math.ceil(baseCost / numGenerations),
      requestCount: numGenerations,
      isMultiGeneration: true
    };
  } else {
    // Formula returns per-generation cost - multiply for total
    return {
      totalCost: baseCost * numGenerations,
      perRequestCost: baseCost,
      requestCount: numGenerations,
      isMultiGeneration: true
    };
  }
}

/**
 * Type guard to check if result is multi-generation
 */
export function isMultiGenerationResult(
  result: TokenCostDisplayResult
): result is MultiGenerationCostResult {
  return typeof result === 'object' && result !== null && 'isMultiGeneration' in result;
}
